/**
 * API Implementation of IMockExamService
 * Maps backend DTOs to frontend domain types
 *
 * Backend endpoints:
 * - GET  /app/mock-exams                       → list active mock exams
 * - GET  /app/mock-exam/:mockExamId            → overview (metadata + attempts)
 * - POST /app/mock-exam/:mockExamId/start      → start new mockExam attempt
 * - POST /app/mock-exam/submit                 → submit all answers
 * - GET  /app/mock-exam/attempts/:attemptId/review → review completed attempt
 */

import { clientApi } from "@/utils/api";
import { TApiRes } from "@/types/api";
import { IMockExamService } from "@/infrastructure/services/mock-exam/mock-exam.service";
import {
  TMockExamListItem,
  TMockExamDetail,
  TMockExamSession,
  TMockExamSubmitRequest,
  TMockExamSubmitResult,
  TMockExamResult,
  TMockExamListItemDto,
  TMockExamDetailDto,
  TStartDto,
  TSubmitDto,
  TReviewDto,
} from "@/infrastructure/adapters/mock-exam/mock-exam.types";
import { mapAttempt, mapListItem, mapReviewSubject, mapStartSubject } from "@/infrastructure/adapters/mock-exam/mock-exam.mapper";

// ─── API Service Implementation ───────────────────────────

const BASE_PATH = "/mock-exam";
const LIST_PATH = "/mock-exams";

export const apiMockExamService: IMockExamService = {
  async getMockExamList(): Promise<TMockExamListItem[]> {
    const { data } = await clientApi.get<TApiRes<TMockExamListItemDto[]>>(LIST_PATH);
    return (data.data ?? []).map(mapListItem);
  },

  async getMockExamDetail(mockExamId: number): Promise<TMockExamDetail> {
    const { data } = await clientApi.get<TApiRes<TMockExamDetailDto>>(`${BASE_PATH}/${mockExamId}`);
    const overview = data.data;

    const totalQuestions = overview.totalQuestions;
    const totalScore = overview.totalScore ?? 0;
    const passingScore = overview.passingScore ?? 0;
    const passingScorePercentage = totalScore > 0 ? Math.round((passingScore / totalScore) * 100) : 0;
    const passingQuestionCount = totalScore > 0 ? Math.ceil((totalQuestions * passingScore) / totalScore) : 0;

    const completedAttempts = overview.attempts.filter((a) => a.status === "completed").map(mapAttempt);

    return {
      mockExamId: overview.mockExamId,
      name: overview.name,
      metadata: {
        totalQuestions,
        timeLimitMinutes: overview.examDuration ?? 0,
        passingScorePercentage,
        passingQuestionCount,
        totalScore,
        passingScore,
      },
      attempts: completedAttempts,
    };
  },

  async createMockExamSession(mockExamId: number): Promise<TMockExamSession> {
    const { data } = await clientApi.post<TApiRes<TStartDto>>(`${BASE_PATH}/${mockExamId}/start`);
    const dto = data.data;

    const finishedAt = new Date(Date.now() + dto.examDuration * 60 * 1000).toISOString();

    return {
      attemptId: dto.attemptId,
      finishedAt,
      subjects: dto.subjects.map(mapStartSubject),
    };
  },

  async submitMockExam(req: TMockExamSubmitRequest): Promise<TMockExamSubmitResult> {
    const { data } = await clientApi.post<TApiRes<TSubmitDto>>(`${BASE_PATH}/submit`, req);
    return data.data;
  },

  async getMockExamResult(attemptId: number): Promise<TMockExamResult> {
    const { data } = await clientApi.get<TApiRes<TReviewDto>>(`${BASE_PATH}/attempts/${attemptId}/review`);
    const dto = data.data;

    const allQuestions = dto.subjects.flatMap((s) => s.questions);
    const correctCount = allQuestions.filter((q) => q.isCorrect === true).length;
    const totalQuestions = allQuestions.length;
    const scorePercentage = dto.totalScore > 0 ? Math.round((dto.earnedScore / dto.totalScore) * 100) : 0;

    return {
      attemptId: dto.attemptId,
      mockExamId: dto.mockExamId,
      grade: dto.grade,
      correctCount,
      totalQuestions,
      scorePercentage,
      earnedScore: dto.earnedScore,
      totalScore: dto.totalScore,
      timeSpentSeconds: dto.timeSpentSeconds,
      subjects: dto.subjects.map(mapReviewSubject),
      finishedAt: dto.finishedAt,
    };
  },
};
